import { useState } from 'react';
import { PlugZapIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/shared/components/EmptyState';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';

import { useAiProviders } from '../hooks/useAiProviders';
import { useCreateAiProvider } from '../hooks/useCreateAiProvider';
import { useDeleteAiProvider } from '../hooks/useDeleteAiProvider';
import { useUpdateAiProvider } from '../hooks/useUpdateAiProvider';
import type { AiProvider } from '../types';
import { AiProviderDeleteDialog } from './AiProviderDeleteDialog';
import { AiProviderFormSheet } from './AiProviderFormSheet';
import type {
  AiProviderFormMode,
  AiProviderFormValues,
} from './AiProviderFormSheet';
import { ClaudeProviderGroup, OtherProviderGroup } from './AiProviderList';
import { SkeletonRows } from '@/shared/components/PendingRows';

/**
 * The "Other Providers" half of the AI Providers section (spec FR-002a).
 *
 * Split out from `AiProvidersSection` so the admin page can place it below
 * the LLM Profiles section while the Claude connections stay above it. It
 * needs no data of its own — `provider_type` values other than `anthropic`
 * aren't creatable yet, so this group is always empty and its "Add
 * provider" affordance always disabled.
 */
export function OtherProvidersSection() {
  return (
    <OtherProviderGroup
      providers={[]}
      onEdit={() => {
        // Never reachable — the group it renders is always empty.
      }}
      onDelete={() => {
        // Never reachable — the group it renders is always empty.
      }}
      onCreateOther={() => {
        // No other provider_type is creatable yet (spec FR-002a) — the
        // button in OtherProviderGroup is disabled, so this never fires.
      }}
    />
  );
}

/**
 * The Claude connections section of the combined admin page (spec
 * FR-001/FR-002).
 *
 * Mirrors `llm-profiles/components/LlmProfilesSection.tsx`'s state machine
 * (loading, load-failed with retry, empty, loaded), scoped to a section
 * rather than a whole page — the page-level frame (title, description) lives
 * in `src/routes/admin/ai-providers.tsx`, which composes this above
 * `LlmProfilesSection`, with `OtherProvidersSection` below it.
 */
export function AiProvidersSection() {
  const { entries, isPending, loadFailed, refetch } = useAiProviders();

  // A placeholder only once the wait is worth remarking on (`079` FR-017).
  const showPlaceholder = usePendingDisplay(isPending);

  const [dialog, setDialog] = useState<AiProviderFormMode | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AiProvider | null>(null);

  const create = useCreateAiProvider();
  const update = useUpdateAiProvider();
  const remove = useDeleteAiProvider();

  const saving = create.isPending || update.isPending;
  const saveError = dialog?.kind === 'edit' ? update.error : create.error;

  const all = entries ?? [];

  function openCreate() {
    create.reset();
    update.reset();
    setDialog({ kind: 'create' });
  }

  function openEdit(entry: AiProvider) {
    create.reset();
    update.reset();
    setDialog({ kind: 'edit', entry });
  }

  function openDelete(entry: AiProvider) {
    remove.reset();
    setPendingDelete(entry);
  }

  function handleSubmit(input: AiProviderFormValues) {
    if (dialog?.kind === 'edit') {
      update.mutate(
        { id: dialog.entry.id, body: input },
        {
          onSuccess: () => {
            setDialog(null);
            toast.success(`Saved ${input.name}.`);
          },
        },
      );
      return;
    }

    create.mutate(input, {
      onSuccess: () => {
        setDialog(null);
        toast.success(`Added ${input.name}.`);
      },
    });
  }

  function handleDelete() {
    if (!pendingDelete) return;
    const { id, name } = pendingDelete;
    remove.mutate(id, {
      onSuccess: () => {
        setPendingDelete(null);
        toast.success(`Deleted ${name}.`);
      },
    });
  }

  if (isPending) {
    if (!showPlaceholder) return null;
    return (
      <div
        aria-busy="true"
        aria-label="Loading AI Providers"
        data-testid="ai-providers-region"
      >
        <SkeletonRows label="Loading AI providers" />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div data-testid="ai-providers-region">
        <EmptyState
          icon={PlugZapIcon}
          description="The AI Providers could not be loaded."
          action={
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div data-testid="ai-providers-region">
      <ClaudeProviderGroup
        providers={all}
        onEdit={openEdit}
        onDelete={openDelete}
        onCreate={openCreate}
      />

      {dialog ? (
        <AiProviderFormSheet
          key={dialog.kind === 'edit' ? `edit:${dialog.entry.id}` : 'create'}
          mode={dialog}
          open
          isPending={saving}
          error={saveError}
          onSubmit={handleSubmit}
          onClearError={dialog.kind === 'edit' ? update.reset : create.reset}
          onCancel={() => {
            setDialog(null);
          }}
        />
      ) : null}

      {pendingDelete ? (
        <AiProviderDeleteDialog
          name={pendingDelete.name}
          open
          isPending={remove.isPending}
          error={remove.error}
          onConfirm={handleDelete}
          onCancel={() => {
            setPendingDelete(null);
            remove.reset();
          }}
        />
      ) : null}
    </div>
  );
}
